import React, { useEffect, useState } from 'react';

import { Form, FormGroup, InputGroup } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AxiosError } from 'axios';
import Select, { ValueType } from 'react-select';

import { useFetchData } from '../../../infrastructure/hooks/useFetchData';
import * as Service from '../../../services/OrdersService';
import { OrderPaymentDetails, Reason } from '../../../types/Order';
import { getAmountToTransfer } from '../helpers';
import { ApiStatus } from '../../../infrastructure/constants/apiStatus';
import { convertNumberToUSD } from '../../../infrastructure/utils/common';
import Modal from '../../utils/ModalComponet';
import LedgerEntry from '../../../types/LedgerEntry';
import PriceBreakdown from '../PriceBreakdown';

import PaymentDetails from './PaymentDetails';

import '../../../styles/components/Modal.css';
import '../../../styles/components/ApproveRefundModal.css';

export type ApproveRefundModalProps = {
  orderId: string;
  status: string;
  tz: string;

  ledgerEntries: LedgerEntry[];
  refundLimits: {
    refund: number;
    reverse: number;
  };

  onClose: (confirmed: boolean) => void;
};

type FormData = {
  refund?: number;
  reverse?: number;
  relatedLedgerEntry?: ValueType<LedgerEntry, false>;
  reason?: Reason;
  notes?: string;
};
type ValidFormData = {
  refund: number;
  reverse?: number;
  relatedLedgerEntry: NonNullable<ValueType<LedgerEntry, false>>;
  reason?: Reason;
  notes?: string;
};

const getOptionLabel = (entry: LedgerEntry | undefined): string => {
  if (!entry) return '';

  return `Refunded: ${convertNumberToUSD(
    entry.totalAmount / 100
  )}, Reversed: ${convertNumberToUSD(getAmountToTransfer(entry) / 100)}`;
};
const getOptionValue = (entry: LedgerEntry): string => {
  return entry.id || '';
};

const notesRequiredForDifferentAmounts = (
  notes: string | undefined,
  data: FormData
): boolean | string => {
  const { refund, reverse, relatedLedgerEntry: entry } = data;

  const invalid =
    (!notes || notes === parseNotes(entry?.notes, 'Reason note: ')) &&
    entry &&
    ((refund && Math.round(refund * 100) !== -entry.totalAmount) ||
      (reverse && Math.round(reverse * 100) !== -getAmountToTransfer(entry)));

  return (
    !invalid ||
    'Notes required if amounts are different from related ledger entry'
  );
};

const parseNotes = (notes: string[] | undefined, prefix: string): string =>
  (notes || []).find(note => note.startsWith(prefix))?.replace(prefix, '') ||
  '';

export default function ApproveRefundModal(props: ApproveRefundModalProps) {
  const [apiStatus, paymentDetails, fetchPaymentDetails] = useFetchData<
    OrderPaymentDetails[]
  >();
  const [priceBreakdownVisible, setPriceBreakdownVisible] = useState<boolean>(
    false
  );

  const {
    register,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    handleSubmit,
    setError,
    setValue,
    getValues,
    clearErrors,
    trigger,
    control,
    watch,
  } = useForm<FormData, undefined, ValidFormData>({
    defaultValues: {},
    mode: 'onChange',
  });

  useEffect(() => {
    fetchPaymentDetails(() => Service.getOrderPaymentDetails(props.orderId));
  }, [fetchPaymentDetails, props.orderId]);

  const handleConfirmClick = async (data: ValidFormData) => {
    clearErrors();

    if (!data.relatedLedgerEntry.id) {
      setError('relatedLedgerEntry', {
        message: 'Please select the requested refund for approval',
      });
      return;
    }

    try {
      await Service.refundOrder(props.orderId, {
        refundAmount: Math.round(data.refund * 100),
        reverseAmount: Math.round((data.reverse || 0) * 100),
        relatedLedgerEntryId: data.relatedLedgerEntry.id,
        reason: data.reason || 'other',
        reasonNote: data.notes,
      });

      props.onClose(true);
    } catch (e) {
      const error = e as AxiosError;
      if (error.isAxiosError) {
        setError('root.server', {
          type: (error.response?.status || 500).toString(),
          message: error.response?.data,
        });
      } else {
        setError('root.server', {
          type: '500',
          message: 'Error occured during partial refund submission',
        });
      }

      // hack to unblock 'Confirm' button after server error
      // react-hook-form set 'isValid' to false when 'setError' called manually
      // but fields validation (since everything is valid), mark form state as valid again
      // 'trigger' is re-validate all fields
      trigger();
    }
  };
  const handleCancelClick = () => props.onClose(false);
  const isValidStatus =
    props.status === 'Cancelled' || props.status === 'Fulfilled';
  const relatedLedgerEntry = getValues('relatedLedgerEntry');

  const handleRelatedLedgerEntryChange = (
    entry: ValueType<LedgerEntry, false>
  ) => {
    if (!entry) {
      setValue('refund', undefined);
      setValue('reverse', undefined);
      setValue('notes', undefined);
      setPriceBreakdownVisible(false);
      setError('relatedLedgerEntry', {
        message: 'Related ledger entry is required',
      });
    } else {
      setValue('refund', -entry.totalAmount / 100);
      setValue('reverse', -getAmountToTransfer(entry) / 100);
      setValue('notes', parseNotes(entry.notes, 'Reason note: '));
    }

    trigger();
  };

  const handlePriceBreakdownClick = () => {
    setPriceBreakdownVisible(!priceBreakdownVisible);
  };

  const refundInput = register('refund', {
    valueAsNumber: true,
    required: {
      value: true,
      message: 'Refund amount is required',
    },
    max: {
      value: props.refundLimits.refund / 100,
      message: 'The maximum refund amount must not exceed the total cost',
    },
    min: {
      value: 0,
      message: "Refund amount can't be negative",
    },
  });
  const reverseInput = register('reverse', {
    valueAsNumber: true,
    max: {
      value: props.refundLimits.reverse / 100,
      message: 'The maximum amount to reverse must not exceed the total',
    },
    min: {
      value: 0,
      message: "Reverse amount can't be negative",
    },
  });

  const watchedRelatedLedgerEntry = watch('relatedLedgerEntry');

  return (
    <Modal
      open={true}
      closeModal={handleCancelClick}
      classNames={{
        modal: `approve-refund-modal ${
          priceBreakdownVisible ? 'approve-refund-modal--breakdown-shown' : ''
        }`,
      }}
    >
      <header>
        <h4>Requested Refund Approval</h4>
      </header>
      <main className='approve-refund-modal__body'>
        {apiStatus === ApiStatus.error && (
          <div>An error occurred while loading payment details.</div>
        )}
        {apiStatus !== ApiStatus.error && (
          <>
            <section className='approve-refund-modal__content'>
              {!isValidStatus && (
                <div className='alert alert-warning'>
                  Refund is not allowed for <b>{props.status}</b> orders
                </div>
              )}
              {errors.root && (
                <div className='alert alert-danger'>
                  Error code: {errors.root['server'].type}
                  <br />
                  Error message: {errors.root['server'].message}
                </div>
              )}
              {isSubmitSuccessful && (
                <div className='alert alert-success'>
                  Partial refund had been successfully submitted
                </div>
              )}
              <section className='approve-refund-modal__form'>
                <Form
                  onSubmit={handleSubmit(handleConfirmClick)}
                  id='approveRefundModalForm'
                >
                  <FormGroup>
                    <p>
                      Requested partial refund:
                      {relatedLedgerEntry && (
                        <button
                          type='button'
                          className='btn btn-link pull-right price-breakdown-link'
                          onClick={handlePriceBreakdownClick}
                        >
                          {priceBreakdownVisible ? 'Hide' : 'Show'} Price
                          Breakdown
                        </button>
                      )}
                    </p>
                    <Controller
                      name='relatedLedgerEntry'
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message:
                            'Please select the requested refund for approval',
                        },
                      }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          onChange={e => {
                            handleRelatedLedgerEntryChange(e);
                            field.onChange(e);
                          }}
                          onBlur={() => {
                            field.onBlur();

                            //re-validate notes field as dependant on 'related ledger entry'
                            trigger('notes');
                          }}
                          options={props.ledgerEntries}
                          getOptionLabel={getOptionLabel}
                          getOptionValue={getOptionValue}
                          openMenuOnFocus={false}
                          styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                        />
                      )}
                    />
                    {errors.relatedLedgerEntry?.message && (
                      <small className='text-danger'>
                        {errors.relatedLedgerEntry?.message}
                      </small>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <p>
                      Refund reason:{' '}
                      <i>
                        {parseNotes(
                          watchedRelatedLedgerEntry?.notes,
                          'Reason: '
                        )}
                      </i>
                    </p>
                  </FormGroup>
                  <FormGroup>
                    <p>
                      Amount to refund user:{' '}
                      <small className='text-muted'>
                        maximum{' '}
                        {convertNumberToUSD(props.refundLimits.refund / 100)}
                      </small>
                    </p>
                    <InputGroup>
                      <input
                        type='number'
                        className='form-control'
                        placeholder='0.00'
                        step={0.01}
                        {...refundInput}
                        onChange={e => {
                          refundInput.onChange(e);

                          //re-validate notes field as dependant on 'refund' amount
                          trigger('notes');
                        }}
                      />
                      <InputGroup.Addon>$</InputGroup.Addon>
                    </InputGroup>
                    {errors.refund?.message && (
                      <small className='text-danger'>
                        {errors.refund?.message}
                      </small>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <p>
                      Amount to reverse from vendor:{' '}
                      <small className='text-muted'>
                        maximum{' '}
                        {convertNumberToUSD(props.refundLimits.reverse / 100)}
                      </small>
                    </p>
                    <InputGroup>
                      <input
                        type='number'
                        className='form-control'
                        placeholder='0.00 (If left blank, no money will be reversed from the vendor Stripe balance)'
                        step={0.01}
                        {...reverseInput}
                        onChange={e => {
                          reverseInput.onChange(e);

                          //re-validate notes field as dependant on 'reverse' amount
                          trigger('notes');
                        }}
                      />
                      <InputGroup.Addon>$</InputGroup.Addon>
                    </InputGroup>
                    {errors.reverse?.message && (
                      <small className='text-danger'>
                        {errors.reverse?.message}
                      </small>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <p>Notes:</p>
                    <textarea
                      rows={3}
                      className='form-control'
                      {...register('notes', {
                        validate: {
                          differentAmounts: notesRequiredForDifferentAmounts,
                        },
                      })}
                    />
                    {errors.notes?.message && (
                      <small className='text-danger'>
                        {errors.notes?.message}
                      </small>
                    )}
                  </FormGroup>
                </Form>
              </section>
              <section className='approve-refund-modal__payment-details'>
                <h4 className='payment-details__header'>Payment details</h4>
                {apiStatus === ApiStatus.loading && (
                  <div className='text-center'>Loading payment details...</div>
                )}
                {apiStatus === ApiStatus.success && (
                  <PaymentDetails
                    paymentDetails={paymentDetails || []}
                    tz={props.tz}
                  />
                )}
              </section>
            </section>
            {priceBreakdownVisible && relatedLedgerEntry && (
              <section className='approve-refund-modal__price-breakdown'>
                <h4 className='price-breakdown-header--mobile'>
                  Price breakdown:
                  {relatedLedgerEntry && (
                    <button
                      type='button'
                      className='btn btn-link pull-right price-breakdown-link'
                      onClick={handlePriceBreakdownClick}
                    >
                      {priceBreakdownVisible ? 'Hide' : 'Show'} Price Breakdown
                    </button>
                  )}
                </h4>
                <PriceBreakdown entry={relatedLedgerEntry} />
              </section>
            )}
          </>
        )}
      </main>
      <footer className='modal__footer'>
        <menu className='modal__actions'>
          <button
            className='btn btn-primary'
            type='submit'
            form='approveRefundModalForm'
            disabled={
              !isValidStatus ||
              apiStatus !== ApiStatus.success ||
              isSubmitting ||
              isSubmitSuccessful
            }
          >
            Confirm
          </button>
          <button
            className='btn btn-secondary'
            type='button'
            onClick={handleCancelClick}
          >
            Cancel
          </button>
        </menu>
      </footer>
    </Modal>
  );
}
